import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";

import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartAnimationProvider } from "@/context/CartAnimationContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { useOnlinePresence } from "@/hooks/useOnlinePresence";
import { Loader2 } from "lucide-react";

// Core pages - loaded immediately
import Index from "./pages/Index";
import DynamicSEO from "./components/DynamicSEO";
import Catalog from "./pages/Catalog";
import NotFound from "./pages/NotFound";
import MobileBottomNav from "./components/MobileBottomNav";
import SiteAlertModal from "./components/SiteAlertModal";
import CookieConsent from "./components/CookieConsent";

// Component that activates online presence tracking inside AuthProvider
const OnlinePresenceTracker = () => { 
  try { useOnlinePresence(); } catch { /* ignore during HMR */ }
  return null; 
};

// Lazy loaded pages - loaded on demand
const About = lazy(() => import("./pages/About"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Favorites = lazy(() => import("./pages/Favorites"));
const SharedList = lazy(() => import("./pages/SharedList"));
const Notifications = lazy(() => import("./pages/Notifications"));
const CartPage = lazy(() => import("./pages/Cart"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Friends = lazy(() => import("./pages/Friends"));
const FriendsMessages = lazy(() => import("./pages/FriendsMessages"));
const ChatView = lazy(() => import("./components/friends/ChatView"));

// Admin pages - lazy loaded (reduces initial bundle significantly)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ProductsAdmin = lazy(() => import("./pages/admin/ProductsAdmin"));
const CategoriesAdmin = lazy(() => import("./pages/admin/CategoriesAdmin"));
const PagesAdmin = lazy(() => import("./pages/admin/PagesAdmin"));
const OrdersAdmin = lazy(() => import("./pages/admin/OrdersAdmin"));
const ReviewsAdmin = lazy(() => import("./pages/admin/ReviewsAdmin"));
const PromocodesAdmin = lazy(() => import("./pages/admin/PromocodesAdmin"));
const BonusAdmin = lazy(() => import("./pages/admin/BonusAdmin"));
const UsersAdmin = lazy(() => import("./pages/admin/UsersAdmin"));
const EconomyAdmin = lazy(() => import("./pages/admin/EconomyAdmin"));
const ReferralAdmin = lazy(() => import("./pages/admin/ReferralAdmin"));
const PartnersAdmin = lazy(() => import("./pages/admin/PartnersAdmin"));
const DeployAdmin = lazy(() => import("./pages/admin/DeployAdmin"));
const MonitoringAdmin = lazy(() => import("./pages/admin/MonitoringAdmin"));
const PostsAdmin = lazy(() => import("./pages/admin/PostsAdmin"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Accountant cabinet (buh.fermerchel74.ru)
const AccountantLogin = lazy(() => import("./pages/accountant/AccountantLogin"));
const AccountantDashboard = lazy(() => import("./pages/accountant/AccountantDashboard"));
const AccountantsAdmin = lazy(() => import("./pages/admin/AccountantsAdmin"));


// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

/** On the buh.* subdomain the root path opens the accountant cabinet. */
const AccountantSubdomainRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isBuh = window.location.hostname.startsWith("buh.");
    if (isBuh && (location.pathname === "/" || location.pathname === "/catalog")) {
      navigate("/buh", { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

/** Hides the shop bottom nav inside the accountant cabinet. */
const ShopChrome = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/buh")) return null;
  return (
    <>
      <MobileBottomNav />
      <SiteAlertModal />
      <CookieConsent />
    </>
  );
};



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <OnlinePresenceTracker />
        <FavoritesProvider>
          <CartProvider>
            <CartAnimationProvider>
              <Toaster />
              <Sonner />
              <DynamicSEO />
              <BrowserRouter>
                <AccountantSubdomainRedirect />
                <Suspense fallback={<PageLoader />}>
                  <Routes>

                    {/* Core routes - loaded immediately */}
                    <Route path="/" element={<Index />} />
                    <Route path="/catalog" element={<Catalog />} />
                    
                    {/* Lazy loaded routes */}
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/product/:id" element={<ProductPage />} />
                    <Route path="/favorites" element={<Favorites />} />
                    <Route path="/shared" element={<SharedList />} />
                    <Route path="/s/:shortId" element={<SharedList />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/notifications" element={<Notifications />} />
                    
                    {/* Friends routes */}
                    <Route path="/friends" element={<Friends />} />
                    <Route path="/friends/messages" element={<FriendsMessages />} />
                    <Route path="/friends/chat/:friendId" element={<ChatView />} />
                    
                    {/* Admin routes - separate chunk */}
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/orders" element={<OrdersAdmin />} />
                    <Route path="/admin/economy" element={<EconomyAdmin />} />
                    <Route path="/admin/users" element={<UsersAdmin />} />
                    <Route path="/admin/products" element={<ProductsAdmin />} />
                    <Route path="/admin/categories" element={<CategoriesAdmin />} />
                    <Route path="/admin/promocodes" element={<PromocodesAdmin />} />
                    <Route path="/admin/bonus" element={<BonusAdmin />} />
                    <Route path="/admin/reviews" element={<ReviewsAdmin />} />
                    <Route path="/admin/pages" element={<PagesAdmin />} />
                    <Route path="/admin/referral" element={<ReferralAdmin />} />
                    <Route path="/admin/partners" element={<PartnersAdmin />} />
                    <Route path="/admin/deploy" element={<DeployAdmin />} />
                    <Route path="/admin/monitoring" element={<MonitoringAdmin />} />
                    <Route path="/admin/posts" element={<PostsAdmin />} />
                    <Route path="/admin/accountants" element={<AccountantsAdmin />} />

                    {/* Accountant cabinet (buh.fermerchel74.ru) */}
                    <Route path="/buh" element={<AccountantDashboard />} />
                    <Route path="/buh/login" element={<AccountantLogin />} />

                    {/* Partner route */}
                    <Route path="/partner" element={<PartnerDashboard />} />
                    
                    {/* OAuth 2.1 consent screen for MCP clients */}
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                    {/* Invite page - dynamic code in URL */}
                    <Route path="/:code" element={<InvitePage />} />
                    
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <ShopChrome />
                </Suspense>

              </BrowserRouter>
            </CartAnimationProvider>
          </CartProvider>
        </FavoritesProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
